import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const footerRef = useRef(null);
  const ctaRef = useRef(null);
  const contentRef = useRef(null);

  const links = [
    { label: 'Install Extension', href: 'https://github.com/Mohammed-3tef/CommitWatch/releases/latest' },
    { label: 'GitHub Repo', href: 'https://github.com/Mohammed-3tef/CommitWatch' },
    { label: 'Documentation', href: '/docs', internal: true },
    { label: 'Report Bug', href: 'https://github.com/Mohammed-3tef/CommitWatch/issues' },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // CTA Box animation
      gsap.fromTo(ctaRef.current,
        { y: 80, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: ctaRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // CTA content stagger
      const ctaChildren = ctaRef.current?.querySelectorAll('.cta-item') || [];
      gsap.fromTo(ctaChildren,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: ctaRef.current,
            start: 'top 75%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Glow pulse effect
      gsap.to('.cta-glow', {
        scale: 1.3,
        opacity: 0.4,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });

      // Footer content animation
      const footerItems = contentRef.current?.querySelectorAll('.footer-item') || [];
      gsap.fromTo(footerItems,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: contentRef.current,
            start: 'top 90%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Social icons animation
      const socialIcons = contentRef.current?.querySelectorAll('.social-icon') || [];
      gsap.fromTo(socialIcons,
        { scale: 0, rotation: -180 },
        {
          scale: 1,
          rotation: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'back.out(1.7)',
          scrollTrigger: {
            trigger: contentRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Bottom bar slide in
      gsap.fromTo('.footer-bottom',
        { y: 20, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.footer-bottom',
            start: 'top 98%',
            toggleActions: 'play none none reverse',
          },
        }
      );

    }, footerRef);

    return () => ctx.revert();
  }, []);

  // Simple hover effect for buttons - no expensive mouse tracking
  const handleButtonHover = (e) => {
    gsap.to(e.currentTarget, {
      scale: 1.05,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  const handleButtonLeave = (e) => {
    gsap.to(e.currentTarget, {
      scale: 1,
      duration: 0.3,
      ease: 'power2.out',
    });
  };
  
  // Link hover animation
  const handleLinkHover = (e) => {
    gsap.to(e.currentTarget, {
      x: 8,
      color: '#22d3ee',
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  const handleLinkLeave = (e) => {
    gsap.to(e.currentTarget, {
      x: 0,
      color: '#6b7280',
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  return (
    <footer
      ref={footerRef}
      className="relative pt-16 sm:pt-24 md:pt-32 pb-8 sm:pb-10 md:pb-12 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-dark-950" />
      <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-dark-900/20 to-transparent" />
      
      {/* Top gradient line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-500/50 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
        {/* CTA Section */}
        <div
          ref={ctaRef}
          className="relative text-center mb-12 sm:mb-16 md:mb-20 p-6 sm:p-10 md:p-14 rounded-2xl sm:rounded-3xl glass border border-white/10 overflow-hidden"
        >
          {/* Glow effect */}
          <div className="cta-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[250px] sm:w-[300px] md:w-[400px] h-[150px] sm:h-[180px] md:h-[200px] bg-accent-500/20 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />
          
          <h2 className="cta-item relative text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 px-2">
            Ready to Never Miss a <span className="gradient-text">Commit</span>?
          </h2>
          <p className="cta-item relative text-base sm:text-lg md:text-xl text-dark-400 mb-6 sm:mb-8 md:mb-10 max-w-xl mx-auto px-2">
            Join thousands of developers who stay in sync with their favorite repos.
          </p>
          <div className="cta-item relative flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 md:gap-5">
            <a
              href="https://github.com/Mohammed-3tef/CommitWatch/releases/latest"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative w-full sm:w-auto px-6 sm:px-8 md:px-10 py-4 sm:py-5 bg-gradient-to-r from-accent-500 to-cyan-400 text-dark-950 font-bold text-base sm:text-lg rounded-xl sm:rounded-2xl overflow-hidden hover:scale-105 transition-transform"
              onMouseEnter={handleButtonHover}
              onMouseLeave={handleButtonLeave}
            >
              <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3">
                Get Started — It's Free
                <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                </svg>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-accent-400 to-cyan-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </a>
            <a
              href="https://github.com/Mohammed-3tef/CommitWatch"
              target="_blank"
              rel="noopener noreferrer"
              className="group w-full sm:w-auto px-6 sm:px-8 md:px-10 py-4 sm:py-5 glass text-white font-semibold text-base sm:text-lg rounded-xl sm:rounded-2xl border border-white/20 hover:border-accent-500/50 hover:scale-105 transition-all duration-300"
              onMouseEnter={handleButtonHover}
              onMouseLeave={handleButtonLeave}
            >
              <span className="flex items-center justify-center gap-2 sm:gap-3">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                </svg>
                View Source Code
              </span>
            </a>
          </div>
        </div>

        {/* Footer content */}
        <div ref={contentRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-10 md:mb-12">
          {/* Brand */}
          <div className="footer-item sm:col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-accent-500 to-cyan-400 rounded-lg sm:rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 sm:w-7 sm:h-7 text-dark-950" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.824a9.176 9.176 0 110 18.352 9.176 9.176 0 010-18.352zM7.412 12a4.588 4.588 0 119.176 0 4.588 4.588 0 01-9.176 0z"/>
                </svg>
              </div>
              <span className="text-xl sm:text-2xl font-bold text-white">Commit Watch</span>
            </div>
            <p className="text-dark-400 leading-relaxed mb-4 sm:mb-6 text-base sm:text-lg">
              Stay in sync with the repos that matter. Smart GitHub commit notifications for developers.
            </p>
          </div>

          {/* Links */}
          <div className="footer-item sm:col-span-2 md:col-span-2">
            <h3 className="text-xs sm:text-sm font-bold text-dark-300 uppercase tracking-wider mb-4 sm:mb-5">
              Quick Links
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {links.map((link, index) => (
                link.internal ? (
                  <Link
                    key={index}
                    to={link.href}
                    className="text-dark-400 hover:text-accent-400 transition-colors duration-300 text-base sm:text-lg"
                    onMouseEnter={handleLinkHover}
                    onMouseLeave={handleLinkLeave}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-dark-400 hover:text-accent-400 transition-colors duration-300 text-base sm:text-lg"
                    onMouseEnter={handleLinkHover}
                    onMouseLeave={handleLinkLeave}
                  >
                    {link.label}
                  </a>
                )
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="footer-bottom pt-6 sm:pt-8 md:pt-10 border-t border-white/5">
            <p className="text-dark-500 text-xs sm:text-sm text-center">
              © {new Date().getFullYear()} Commit Watch. Open source under MPL License.
            </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
